import { useMemo } from 'react';
import { APITypes } from '../../primary-record-types';
import { useQuery } from '@tanstack/react-query';
import { getIntl } from '../../utils/intl';
import { getFDASearchCacheKey } from './cacheKeys';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useQueryErrorNotify } from '../useQueryCallbacks';

const intl = getIntl('UseFDADrugSearch', {
    searchFailure: 'Failed to search the FDA database',
});

export type NameAndActiveIngredient = {
    name: string;
    genericNameOrActiveIngredient: string[];
};

export type FDASearchValue = {
    nameAndActiveIngredient: string | NameAndActiveIngredient | null;
    strength: string | null;
    form: string | null;
    route: string | null;
};

export type FDASearchResultValue = {
    nameAndActiveIngredient: NameAndActiveIngredient[];
    strength: string[];
    form: string[];
    route: string[];
};

export type Fields = keyof FDASearchValue;

export type UseFDADrugSearchArgs = {
    value: FDASearchValue;
};

export type UseFDADrugSearchReturnValue = {
    searchResults: FDASearchResultValue;
    isLoading: boolean;
};

export const getNameAndActiveIngredientKey = (value: NameAndActiveIngredient): string => {
    return `${value.name} - ${[...value.genericNameOrActiveIngredient]
        .sort()
        .map((_) => _)
        .join(', ')}`;
};

export function useFDADrugSearch({ value }: UseFDADrugSearchArgs): UseFDADrugSearchReturnValue {
    const request = useMemo<APITypes.medicalDataService.SearchFDADrugsRequest | null>(() => {
        const isValidSearchInput = (value: string | null): value is string => !!value && value.trim().length >= 3;

        if (value.nameAndActiveIngredient === null) return null;

        if (typeof value.nameAndActiveIngredient === 'string') {
            if (!isValidSearchInput(value.nameAndActiveIngredient)) {
                return null;
            } else {
                return {
                    type: 'partial',
                    searchString: value.nameAndActiveIngredient,
                };
            }
        }

        return {
            type: 'exact',
            brandName: value.nameAndActiveIngredient.name,
            activeIngredients: value.nameAndActiveIngredient.genericNameOrActiveIngredient,
            dosageForm: value.form || undefined,
            route: value.route || undefined,
        };
    }, [value]);

    const fdaSearchQuery = useQuery({
        queryKey: getFDASearchCacheKey(request || { type: 'partial', searchString: '' }),
        queryFn: async () => {
            return await appAPI.post<APITypes.medicalDataService.SearchFDADrugsResponse>(
                urls.api.medicalData.fdaDrugSearch,
                { body: request },
            );
        },
        enabled: !!request,
    });
    useQueryErrorNotify(fdaSearchQuery, intl('searchFailure'));

    const searchResults = useMemo<FDASearchResultValue>(() => {
        if (!fdaSearchQuery.data) {
            return {
                nameAndActiveIngredient: [],
                strength: [],
                form: [],
                route: [],
            };
        }

        const nameAndActiveIngredientsMap: Record<string, NameAndActiveIngredient> = {};
        fdaSearchQuery.data.items.forEach((result) => {
            const nameAndActiveIngredient: NameAndActiveIngredient = {
                name: result.brandName,
                genericNameOrActiveIngredient: result.activeIngredients.map((_) => _.name),
            };
            nameAndActiveIngredientsMap[getNameAndActiveIngredientKey(nameAndActiveIngredient)] =
                nameAndActiveIngredient;
        });

        return {
            nameAndActiveIngredient: Object.values(nameAndActiveIngredientsMap),
            strength: [
                ...new Set(fdaSearchQuery.data.items.map((_) => _.activeIngredients.map((_) => _.strength).join(', '))),
            ],
            form: [...new Set(fdaSearchQuery.data.items.map((_) => _.dosageForm))],
            route: [...new Set(fdaSearchQuery.data.items.map((_) => _.route))],
        };
    }, [fdaSearchQuery.data]);

    return {
        searchResults,
        isLoading: fdaSearchQuery.isLoading,
    };
}
